import React, { useId, useMemo } from 'react';
import { computeProfitSeries, formatMoney } from '../../utils/betStats';
import './BetsSummary.css';

const CHART_W = 600;
const CHART_H = 140;
const PAD_Y = 10;

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = String(dateStr).split('T')[0].split('-');
  return d && m && y ? `${d}/${m}/${y.slice(2)}` : String(dateStr);
}

// Cumulative profit line with a dashed break-even line at 0€
function ProfitChart({ series, positive }) {
  const gradientId = useId();

  const geometry = useMemo(() => {
    const values = [0, ...series.map(p => p.value)];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const x = (i) => (i / (values.length - 1)) * CHART_W;
    const y = (v) => PAD_Y + ((max - v) / range) * (CHART_H - PAD_Y * 2);
    const points = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`);
    return {
      line: `M${points.join(' L')}`,
      area: `M0,${CHART_H} L${points.join(' L')} L${CHART_W},${CHART_H} Z`,
      zeroY: y(0),
    };
  }, [series]);

  return (
    <figure className={`overview-chart ${positive ? 'is-pos' : 'is-neg'}`}>
      <svg viewBox={`0 0 ${CHART_W} ${CHART_H}`} preserveAspectRatio="none" role="img" aria-label="Evolução do profit">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.18" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>
        <line className="overview-chart-zero" x1="0" x2={CHART_W} y1={geometry.zeroY} y2={geometry.zeroY} />
        <path d={geometry.area} fill={`url(#${gradientId})`} />
        <path className="overview-chart-line" d={geometry.line} />
      </svg>
      <figcaption>
        <span>{formatShortDate(series[0].date)}</span>
        <span>Profit acumulado</span>
        <span>{formatShortDate(series[series.length - 1].date)}</span>
      </figcaption>
    </figure>
  );
}

// Profit headline + cumulative chart + a ruled strip of secondary figures.
// `stats` comes from computeBetStats(); `bets` is the same (filtered) list.
function BetsSummary({ stats, bets = [] }) {
  const {
    wonBets, lostBets, pendingBets, voidBets,
    totalStake, totalWinnings, profit, winRate,
  } = stats;
  const positive = profit >= 0;
  const series = useMemo(() => computeProfitSeries(bets), [bets]);

  const recordNotes = [
    pendingBets > 0 && `${pendingBets} ${pendingBets === 1 ? 'pendente' : 'pendentes'}`,
    voidBets > 0 && `${voidBets} ${voidBets === 1 ? 'anulada' : 'anuladas'}`,
  ].filter(Boolean);

  return (
    <section className="bets-overview" aria-label="Resumo">
      <div className="overview-main">
        <div className="overview-lead">
          <span className="overview-label">Profit</span>
          <span className={`overview-profit ${positive ? 'is-pos' : 'is-neg'}`}>
            {formatMoney(profit, { sign: true })}
          </span>
        </div>

        {series.length >= 2 ? (
          <ProfitChart series={series} positive={positive} />
        ) : (
          <div className="overview-chart overview-chart-empty">Sem apostas resolvidas neste período.</div>
        )}
      </div>

      <dl className="overview-strip">
        <div className="overview-cell">
          <dt>Win rate</dt>
          <dd>{winRate.toFixed(1)}%</dd>
        </div>
        <div className="overview-cell">
          <dt>Total apostado</dt>
          <dd>{formatMoney(totalStake)}</dd>
        </div>
        <div className="overview-cell">
          <dt>Total ganho</dt>
          <dd>{formatMoney(totalWinnings)}</dd>
        </div>
        <div className="overview-cell">
          <dt>Ganhas / Perdidas</dt>
          <dd>
            <span className="rec-won">{wonBets}</span>
            <span className="rec-sep">/</span>
            <span className="rec-lost">{lostBets}</span>
            {recordNotes.length > 0 && <span className="rec-notes">{recordNotes.join(' · ')}</span>}
          </dd>
        </div>
      </dl>
    </section>
  );
}

export default BetsSummary;
